import type {
  ServiceBusinessOrder,
  ServiceBusinessOrderStatus
} from '@/feature/service-business/data/serviceBusinessConfig';
import { serviceBusinessPaths } from '@/feature/service-business/data/serviceBusinessConfig';
import { getStoredTenantId } from '@/feature/auth/utils/tenantStorage';

export type NotificationCategory = 'order' | 'message' | 'reminder' | 'system';
export type NotificationStatus = 'unread' | 'read';
export type NotificationPriority = 'normal' | 'high';

export interface NotificationCenterItem {
  id: string;
  category: NotificationCategory;
  title: string;
  description: string;
  createdAt: string;
  status: NotificationStatus;
  priority: NotificationPriority;
  href?: string;
  actionLabel?: string;
  meta?: {
    orderId?: string;
    customer?: string;
    phone?: string;
  };
}

type NotificationCenterDraft = Omit<NotificationCenterItem, 'status'> & {
  defaultStatus?: NotificationStatus;
};

export const notificationCenterUpdatedEvent =
  'hsdesk-notification-center-updated';

const notificationReadStorageKey = 'hsdesk-notification-read-ids';
const backofficeBasePath = serviceBusinessPaths.backofficeBasePath;
let notificationOrderCache: ServiceBusinessOrder[] = [];
let realtimeNotificationCache: NotificationCenterItem[] = [];

const statusLabels: Record<string, string> = {
  new: 'Nowe',
  scheduled: 'Umówione',
  in_progress: 'W realizacji',
  ready: 'Gotowe'
};

function canUseStorage() {
  return typeof window !== 'undefined' && Boolean(window.localStorage);
}

function emitNotificationCenterUpdate() {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(notificationCenterUpdatedEvent));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readStringValue(
  value: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'string' && item.trim()) {
      return item.trim();
    }

    if (typeof item === 'number' && Number.isFinite(item)) {
      return String(item);
    }
  }

  return undefined;
}

function readBooleanValue(
  value: Record<string, unknown>,
  keys: string[]
): boolean | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'boolean') {
      return item;
    }
  }

  return undefined;
}

function getNotificationIdFromEvent(value: unknown) {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (!isRecord(value)) {
    return undefined;
  }

  return readStringValue(value, ['id', 'notificationId', 'appNotificationId']);
}

function getTenantScopedStorageKey(baseKey: string) {
  const tenantId = getStoredTenantId();

  return tenantId ? `${baseKey}:${tenantId}` : baseKey;
}

function parseReadIds(value: string | null): string[] {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
}

function getReadNotificationIds() {
  if (!canUseStorage()) return [];

  return parseReadIds(
    window.localStorage.getItem(
      getTenantScopedStorageKey(notificationReadStorageKey)
    )
  );
}

function saveReadNotificationIds(ids: string[]) {
  if (!canUseStorage()) return;

  window.localStorage.setItem(
    getTenantScopedStorageKey(notificationReadStorageKey),
    JSON.stringify(Array.from(new Set(ids)))
  );
}

function normalizeNotificationOrder(
  order: ServiceBusinessOrder
): ServiceBusinessOrder {
  return {
    id: order.id,
    number: order.number,
    customer: order.customer,
    phone: order.phone,
    subject: order.subject,
    service: order.service,
    date: order.date,
    scheduledAt: order.scheduledAt,
    value: order.value,
    status: order.status,
    note: order.note
  };
}

function getNotificationOrders() {
  return notificationOrderCache;
}

function getIsoMinutesAgo(minutes: number) {
  return new Date(Date.now() - minutes * 60 * 1000).toISOString();
}

function getOrderHref(orderId: string) {
  return `${backofficeBasePath}/orders/${encodeURIComponent(orderId)}`;
}

function getStatusLabel(status: ServiceBusinessOrderStatus) {
  return statusLabels[status] ?? status;
}

function normalizeNotificationCategory(
  value: Record<string, unknown>
): NotificationCategory {
  const discriminator = [
    readStringValue(value, ['category']),
    readStringValue(value, ['sourceType']),
    readStringValue(value, ['type'])
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (discriminator.includes('order') || discriminator.includes('zlecen')) {
    return 'order';
  }

  if (
    discriminator.includes('reminder') ||
    discriminator.includes('sms') ||
    discriminator.includes('calendar')
  ) {
    return 'reminder';
  }

  if (
    discriminator.includes('message') ||
    discriminator.includes('contact') ||
    discriminator.includes('chat')
  ) {
    return 'message';
  }

  return 'system';
}

function normalizeNotificationPriority(
  value: Record<string, unknown>
): NotificationPriority {
  const severity = [
    readStringValue(value, ['priority']),
    readStringValue(value, ['severity']),
    readStringValue(value, ['type'])
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return /critical|error|warning|warn|high|urgent|pilne/.test(severity)
    ? 'high'
    : 'normal';
}

function getNotificationHref(
  value: Record<string, unknown>,
  category: NotificationCategory
) {
  const url = readStringValue(value, ['url', 'href', 'link']);

  if (url?.startsWith('/')) {
    return url;
  }

  const sourceId = readStringValue(value, ['sourceId', 'orderId']);
  if (category === 'order' && sourceId) {
    return getOrderHref(sourceId);
  }

  return undefined;
}

function getNotificationMeta(
  value: Record<string, unknown>,
  category: NotificationCategory
) {
  const sourceId = readStringValue(value, ['sourceId', 'orderId']);
  const customer = readStringValue(value, ['customer', 'customerName']);
  const phone = readStringValue(value, ['phone', 'phoneNo', 'phoneNumber']);

  return {
    orderId: category === 'order' ? sourceId : undefined,
    customer,
    phone
  };
}

export function normalizeAppNotification(
  value: unknown
): NotificationCenterItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const category = normalizeNotificationCategory(value);
  const createdAt =
    readStringValue(value, ['createdAt', 'timestamp', 'sentAt']) ??
    new Date().toISOString();
  const title = readStringValue(value, ['title', 'subject']) ?? 'Powiadomienie';
  const description =
    readStringValue(value, ['description', 'content', 'message', 'body']) ??
    title;
  const id =
    getNotificationIdFromEvent(value) ??
    `app:${category}:${createdAt}:${title}`.toLowerCase();
  const status = readStringValue(value, ['status'])?.toLowerCase();
  const isRead =
    readBooleanValue(value, ['isRead', 'read']) ??
    (Boolean(readStringValue(value, ['readOn', 'readAt'])) ||
      status === 'read' ||
      status === 'readed');

  return {
    id,
    category,
    title,
    description,
    createdAt,
    status: isRead ? 'read' : 'unread',
    priority: normalizeNotificationPriority(value),
    href: getNotificationHref(value, category),
    actionLabel:
      readStringValue(value, ['actionLabel', 'action', 'buttonLabel']) ??
      (category === 'order' ? 'Otwórz zlecenie' : undefined),
    meta: getNotificationMeta(value, category)
  };
}

function createOrderNotification(
  order: ServiceBusinessOrder,
  index: number
): NotificationCenterDraft {
  return {
    id: `order:new:${order.id}`,
    category: 'order',
    title: `Nowe zlecenie ${order.number}`,
    description: `${order.customer}, ${order.subject} - ${order.service}. Status: ${getStatusLabel(
      order.status
    )}.`,
    createdAt: getIsoMinutesAgo(6 + index * 12),
    priority: 'high',
    defaultStatus: 'unread',
    href: getOrderHref(order.id),
    actionLabel: 'Otwórz zlecenie',
    meta: {
      orderId: order.id,
      customer: order.customer,
      phone: order.phone
    }
  };
}

function createReadyReminder(
  order: ServiceBusinessOrder,
  index: number
): NotificationCenterDraft {
  return {
    id: `reminder:ready:${order.id}`,
    category: 'reminder',
    title: `Zlecenie ${order.number} gotowe do odbioru`,
    description: `Sprawdź komunikację z klientem: ${order.customer}, ${order.subject}.`,
    createdAt: getIsoMinutesAgo(24 + index * 15),
    priority: 'high',
    defaultStatus: 'unread',
    href: getOrderHref(order.id),
    actionLabel: 'Sprawdź status',
    meta: {
      orderId: order.id,
      customer: order.customer,
      phone: order.phone
    }
  };
}

function createNotificationDrafts(): NotificationCenterDraft[] {
  const orders = getNotificationOrders();
  const orderNotifications = orders
    .filter((order) => order.status === 'new')
    .map(createOrderNotification);
  const reminderNotifications = orders
    .filter((order) => order.status === 'ready')
    .map(createReadyReminder);

  return [...orderNotifications, ...reminderNotifications];
}

function applyReadState(
  draft: NotificationCenterDraft,
  readIds: Set<string>
): NotificationCenterItem {
  const { defaultStatus = 'unread', ...notification } = draft;

  return {
    ...notification,
    status: readIds.has(draft.id) ? 'read' : defaultStatus
  };
}

export function getNotificationCenterItems() {
  const readIds = new Set(getReadNotificationIds());
  const realtimeNotifications = realtimeNotificationCache.map((notification) =>
    readIds.has(notification.id)
      ? { ...notification, status: 'read' as const }
      : notification
  );
  const generatedNotifications = createNotificationDrafts().map((draft) =>
    applyReadState(draft, readIds)
  );

  return [...realtimeNotifications, ...generatedNotifications]
    .filter(
      (notification, index, notifications) =>
        notifications.findIndex((item) => item.id === notification.id) === index
    )
    .sort((first, second) => {
      if (first.status !== second.status) {
        return first.status === 'unread' ? -1 : 1;
      }

      return (
        new Date(second.createdAt).getTime() -
        new Date(first.createdAt).getTime()
      );
    });
}

export function markNotificationAsRead(notificationId: string) {
  const nextReadIds = [...getReadNotificationIds(), notificationId];

  realtimeNotificationCache = realtimeNotificationCache.map((notification) =>
    notification.id === notificationId
      ? { ...notification, status: 'read' }
      : notification
  );
  saveReadNotificationIds(nextReadIds);
  emitNotificationCenterUpdate();
}

export function markAllNotificationsAsRead(notificationIds?: string[]) {
  const idsToMark =
    notificationIds ?? getNotificationCenterItems().map((item) => item.id);
  const nextReadIds = [...getReadNotificationIds(), ...idsToMark];
  const idsToMarkSet = new Set(idsToMark);

  realtimeNotificationCache = realtimeNotificationCache.map((notification) =>
    idsToMarkSet.has(notification.id)
      ? { ...notification, status: 'read' }
      : notification
  );
  saveReadNotificationIds(nextReadIds);
  emitNotificationCenterUpdate();
}

export function clearNotificationCenterState() {
  notificationOrderCache = [];
  realtimeNotificationCache = [];

  if (canUseStorage()) {
    window.localStorage.removeItem(
      getTenantScopedStorageKey(notificationReadStorageKey)
    );
  }
  emitNotificationCenterUpdate();
}

export function setRealtimeNotificationItems(values: unknown[]) {
  realtimeNotificationCache = values
    .map(normalizeAppNotification)
    .filter((item): item is NotificationCenterItem => Boolean(item))
    .slice(0, 100);
  emitNotificationCenterUpdate();
}

export function upsertRealtimeNotification(value: unknown) {
  const notification = normalizeAppNotification(value);

  if (!notification) {
    return null;
  }

  realtimeNotificationCache = [
    notification,
    ...realtimeNotificationCache.filter((item) => item.id !== notification.id)
  ].slice(0, 100);
  emitNotificationCenterUpdate();

  return notification;
}

export function markRealtimeNotificationAsRead(value: unknown) {
  const notificationId = getNotificationIdFromEvent(value);

  if (!notificationId) {
    return;
  }

  markNotificationAsRead(notificationId);
}

export function deleteRealtimeNotification(value: unknown) {
  const notificationId = getNotificationIdFromEvent(value);

  if (!notificationId) {
    return;
  }

  realtimeNotificationCache = realtimeNotificationCache.filter(
    (notification) => notification.id !== notificationId
  );
  emitNotificationCenterUpdate();
}

export function clearRealtimeNotificationState() {
  realtimeNotificationCache = [];
  emitNotificationCenterUpdate();
}

export function syncNotificationOrders(orders: ServiceBusinessOrder[]) {
  notificationOrderCache = orders.slice(0, 100).map(normalizeNotificationOrder);
  emitNotificationCenterUpdate();
}

export function formatNotificationTime(createdAt: string) {
  const createdAtTime = new Date(createdAt).getTime();
  const diffInMinutes = Math.max(
    0,
    Math.round((Date.now() - createdAtTime) / 60000)
  );

  if (diffInMinutes < 1) return 'przed chwilą';
  if (diffInMinutes < 60) return `${diffInMinutes} min temu`;

  const diffInHours = Math.round(diffInMinutes / 60);

  if (diffInHours < 24) return `${diffInHours} godz. temu`;

  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(createdAtTime);
}
